import React, { useState, useRef, useEffect, useMemo } from 'react';
import { User } from '../../../types';
import { ArrowLeft, Upload, CheckCircle, AlertTriangle, FileText, X, ArrowRight, Save, Database, Loader2, Download } from 'lucide-react';
import Papa from 'papaparse';
import { processCsvToClients, ParsedClient } from '../../../src/utils/csvValidation';
import { api } from '../../../services/api';

interface CsvImportClientsProps {
    user: User;
    onBack: () => void;
    onImportComplete?: () => void;
}

const SYSTEM_FIELDS = [
    { id: 'companyName', label: 'settings:tools.system_fields.companyName', required: true, description: 'settings:tools.system_fields.companyName_desc' },
    { id: 'vatNumber', label: 'settings:tools.system_fields.vatNumber', required: true, description: 'settings:tools.system_fields.vatNumber_desc' },
    { id: 'email', label: 'settings:tools.system_fields.email', required: false, description: 'settings:tools.system_fields.email_desc' },
    { id: 'contactEmail', label: 'settings:tools.system_fields.contactEmail', required: false },
    { id: 'responsiblePerson', label: 'settings:tools.system_fields.responsiblePerson', required: false },
    { id: 'taxOffice', label: 'settings:tools.system_fields.taxOffice', required: false },
    { id: 'businessActivity', label: 'settings:tools.system_fields.businessActivity', required: false },
    { id: 'fullAddress', label: 'settings:tools.system_fields.fullAddress', required: false },
    { id: 'street', label: 'settings:tools.system_fields.street', required: false },
    { id: 'city', label: 'settings:tools.system_fields.city', required: false },
    { id: 'postalCode', label: 'settings:tools.system_fields.postalCode', required: false },
    { id: 'mobilePhone', label: 'settings:tools.system_fields.mobilePhone', required: false },
    { id: 'landline', label: 'settings:tools.system_fields.landline', required: false },
    { id: 'website', label: 'settings:tools.system_fields.website', required: false },
    { id: 'assignedTeamMember', label: 'settings:tools.system_fields.assignedTeamMember', required: false, description: 'settings:tools.system_fields.assignedTeamMember_desc' },
];

type WizardStep = 'upload' | 'mapping' | 'preview' | 'importing' | 'summary';

interface ImportResult {
    totalProcessed: number;
    created: number;
    updated: number;
    failed: number;
    errors: { rowNumber: number, vatNumber: string, reason: string }[];
}

import { useTranslation } from 'react-i18next';

export const CsvImportClients: React.FC<CsvImportClientsProps> = ({ user, onBack, onImportComplete }) => {
    const { t } = useTranslation(['settings', 'common']);
    const [step, setStep] = useState<WizardStep>('upload');
    const [file, setFile] = useState<File | null>(null);
    const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
    const [csvData, setCsvData] = useState<any[]>([]);
    const [mapping, setMapping] = useState<Record<string, string>>({});
    const [error, setError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [sendInvites, setSendInvites] = useState(false);
    const [importResult, setImportResult] = useState<ImportResult | null>(null);

    // Profile States
    const [savedProfiles, setSavedProfiles] = useState<{ id: string, name: string, mapping: Record<string, string> }[]>([]);
    const [selectedProfileId, setSelectedProfileId] = useState<string>('');
    const [isSavingProfile, setIsSavingProfile] = useState(false);
    const [newProfileName, setNewProfileName] = useState('');
    const [showSaveModal, setShowSaveModal] = useState(false);

    const agencyId = user.role === 'agency' ? user.id : user.agencyId;
    useEffect(() => {
        if (csvHeaders.length > 0 && Object.keys(mapping).length === 0) {
            const initialMapping: Record<string, string> = {};
            SYSTEM_FIELDS.forEach(field => {
                const translatedLabel = t(field.label);
                // Try to find an exact or case-insensitive match
                const match = csvHeaders.find(h =>
                    h.toLowerCase() === field.id.toLowerCase() ||
                    h.toLowerCase() === translatedLabel.toLowerCase() ||
                    h.toLowerCase().includes(translatedLabel.toLowerCase())
                );
                if (match) {
                    initialMapping[field.id] = match;
                }
            });
        }
    }, [csvHeaders, t]);

    // Load profiles when reaching mapping step
    useEffect(() => {
        if (step === 'mapping' && agencyId) {
            loadProfiles();
        }
    }, [step, agencyId]);

    const loadProfiles = async () => {
        if (!agencyId) return;
        try {
            const profiles = await api.getMappingProfiles(agencyId);
            setSavedProfiles(profiles);
        } catch (err) {
            console.error("Failed to load profiles:", err);
        }
    };

    const handleProfileSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const id = e.target.value;
        setSelectedProfileId(id);
        if (id) {
            const profile = savedProfiles.find(p => p.id === id);
            if (profile) {
                // Only load mappings that match current CSV headers
                const validMapping: Record<string, string> = {};
                Object.entries(profile.mapping).forEach(([sysField, csvField]) => {
                    if (csvHeaders.includes(csvField)) {
                        validMapping[sysField] = csvField;
                    }
                });
                setMapping(validMapping);
            }
        } else {
            setMapping({});
        }
    };

    const handleSaveProfile = async () => {
        if (!newProfileName.trim() || !agencyId) return;
        setIsSavingProfile(true);
        try {
            const newId = await api.saveMappingProfile(agencyId, newProfileName.trim(), mapping);
            setShowSaveModal(false);
            setNewProfileName('');
            await loadProfiles();
            setSelectedProfileId(newId);
        } catch (err) {
            console.error("Failed to save profile:", err);
            alert(t('tools.error_saving_profile'));
        } finally {
            setIsSavingProfile(false);
        }
    };

    const handleMappingChange = (systemFieldId: string, csvHeader: string) => {
        setMapping(prev => ({
            ...prev,
            [systemFieldId]: csvHeader
        }));
    };

    const isMappingValid = () => {
        return SYSTEM_FIELDS.filter(f => f.required).every(f => !!mapping[f.id]);
    };
    const isValidMapping = isMappingValid();

    const parsedClients = useMemo(() => {
        if (step !== 'preview' && step !== 'importing' && step !== 'summary') return [];
        return processCsvToClients(csvData, mapping);
    }, [csvData, mapping, step]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile) processFile(selectedFile);
    };

    const handleImportSubmit = async () => {
        setStep('importing');
        setError(null);

        try {
            const validClients = parsedClients.filter(c => c.isValid);
            const response = await api.importClientsFromCsv({
                clients: validClients,
                sendInvites
            });

            setImportResult(response.result || response);
            setStep('summary');
            if (onImportComplete) {
                onImportComplete();
            }
        } catch (err: any) {
            setError(err.message || t('tools.error_importing'));
            setStep('preview');
        }
    };

    const downloadErrorsCsv = () => {
        if (!importResult || importResult.errors.length === 0) return;

        const csvContent = Papa.unparse(importResult.errors);
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `import_errors_${new Date().toISOString().slice(0, 10)}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        const droppedFile = e.dataTransfer.files?.[0];
        if (droppedFile) processFile(droppedFile);
    };

    const processFile = (selectedFile: File) => {
        setError(null);
        if (selectedFile.type !== 'text/csv' && !selectedFile.name.endsWith('.csv')) {
            setError(t('tools.error_invalid_csv'));
            return;
        }

        Papa.parse(selectedFile, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
                if (results.errors.length > 0 && results.data.length === 0) {
                    setError(t('tools.error_reading_csv'));
                    return;
                }

                if (results.data.length === 0) {
                    setError(t('tools.error_empty_csv'));
                    return;
                }

                if (results.data.length > 10000) {
                    setError(t('tools.error_row_limit'));
                    return;
                }

                setFile(selectedFile);
                setCsvHeaders(results.meta.fields || []);
                setCsvData(results.data);
            },
            error: (error) => {
                setError(t('tools.error_parse', { message: error.message }));
            }
        });
    };

    const handleRemoveFile = () => {
        setFile(null);
        setCsvHeaders([]);
        setCsvData([]);
        setError(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center gap-4">
                <button
                    onClick={onBack}
                    className="p-2 hover:bg-slate-100 rounded-full transition-colors"
                >
                    <ArrowLeft size={20} />
                </button>
                <div>
                    <h2 className="text-xl font-bold text-slate-800">{t('tools.csv_import_title')}</h2>
                    <p className="text-sm text-slate-500 mt-1">
                        {t('tools.csv_import_main_desc')}
                    </p>
                    <a href="https://accountingflow.eu/wp-content/uploads/2026/02/accountingflow_sample_clients_import.csv" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 font-medium inline-flex items-center gap-1 mt-2">
                        <Download size={14} /> {t('tools.download_sample')}
                    </a>
                </div>
            </div>

            {/* Stepper */}
            <div className="bg-white p-4 rounded-xl border border-slate-200 flex items-center justify-between">
                {[
                    { id: 'upload', label: t('tools.steps.upload') },
                    { id: 'mapping', label: t('tools.steps.mapping') },
                    { id: 'preview', label: t('tools.steps.preview') },
                    { id: 'summary', label: t('tools.steps.summary') }
                ].map((s, i) => (
                    <div key={s.id} className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${step === s.id ? 'bg-black text-white' :
                            (step === 'summary' || i < ['upload', 'mapping', 'preview', 'summary'].indexOf(step)) ? 'bg-green-100 text-green-700' :
                                'bg-slate-100 text-slate-400'
                            }`}>
                            {i + 1}
                        </div>
                        <span className={`text-sm font-medium hidden md:block ${step === s.id ? 'text-black' : 'text-slate-500'}`}>
                            {s.label}
                        </span>
                    </div>
                ))}
            </div>

            {/* Content Area */}
            <div className="bg-white rounded-xl border border-slate-200 p-6 min-h-[400px]">
                {step === 'upload' && (
                    <div className="max-w-2xl mx-auto mt-8 relative">
                        {error && (
                            <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg flex items-start gap-3">
                                <AlertTriangle className="flex-shrink-0 mt-0.5" size={18} />
                                <span className="text-sm font-medium">{error}</span>
                            </div>
                        )}

                        {!file ? (
                            <div
                                className="flex flex-col items-center justify-center p-12 border-2 border-dashed border-slate-300 rounded-2xl hover:border-indigo-400 hover:bg-indigo-50/50 transition-all cursor-pointer group"
                                onClick={() => fileInputRef.current?.click()}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={handleDrop}
                            >
                                <input
                                    type="file"
                                    accept=".csv"
                                    className="hidden"
                                    ref={fileInputRef}
                                    onChange={handleFileChange}
                                />
                                <div className="w-16 h-16 bg-slate-100 text-slate-400 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 group-hover:bg-indigo-100 group-hover:text-indigo-600 transition-all">
                                    <Upload size={32} />
                                </div>
                                <h3 className="text-lg font-bold text-slate-800 mb-2">{t('tools.drop_title')}</h3>
                                <p className="text-sm text-slate-500 text-center max-w-sm mb-6">
                                    {t('tools.drop_subtitle')}
                                </p>
                                <div className="text-xs text-slate-400 bg-slate-50 px-4 py-2 rounded-lg border border-slate-100">
                                    {t('tools.max_limit')}
                                </div>
                            </div>
                        ) : (
                            <div className="p-8 border border-slate-200 rounded-2xl flex flex-col items-center text-center">
                                <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-6">
                                    <FileText size={32} />
                                </div>
                                <h3 className="font-bold text-slate-800 mb-1">{file.name}</h3>
                                <p className="text-sm text-slate-500 mb-6">
                                    {t('tools.file_info', { rows: csvData.length, cols: csvHeaders.length })}
                                </p>

                                <div className="flex items-center gap-3">
                                    <button
                                        onClick={handleRemoveFile}
                                        className="px-5 py-2.5 bg-white border border-slate-200 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-50 hover:text-red-600 transition-colors"
                                    >
                                        {t('common:remove')}
                                    </button>
                                    <button
                                        onClick={() => setStep('mapping')}
                                        className="px-5 py-2.5 bg-black text-white hover:bg-slate-800 rounded-xl text-sm font-bold shadow-lg transition-all"
                                    >
                                        {t('tools.continue_mapping')}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {step === 'mapping' && (
                    <div className="max-w-4xl mx-auto mt-4">
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h3 className="text-lg font-bold text-slate-800">{t('tools.mapping_title')}</h3>
                                <p className="text-sm text-slate-500">
                                    {t('tools.mapping_desc')}
                                </p>
                            </div>
                            <div className="flex gap-2">
                                <select
                                    value={selectedProfileId}
                                    onChange={handleProfileSelect}
                                    className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-black outline-none"
                                >
                                    <option value="">{t('tools.load_mapping_profile')}</option>
                                    {savedProfiles.map(p => (
                                        <option key={p.id} value={p.id}>{p.name}</option>
                                    ))}
                                </select>
                                <button
                                    onClick={() => setShowSaveModal(true)}
                                    disabled={!isValidMapping}
                                    className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                                    <Save size={16} /> {t('tools.save_mapping_profile')}
                                </button>
                            </div>
                        </div>

                        <div className="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden mb-6">
                            <div className="grid grid-cols-2 gap-4 p-4 border-b border-slate-200 bg-slate-100 font-bold text-sm text-slate-700">
                                <div>{t('tools.system_field')}</div>
                                <div>{t('tools.csv_column')}</div>
                            </div>
                            <div className="divide-y divide-slate-200 max-h-[500px] overflow-y-auto">
                                {SYSTEM_FIELDS.map(field => (
                                    <div key={field.id} className="grid grid-cols-2 gap-4 p-4 items-center bg-white hover:bg-slate-50 transition-colors">
                                        <div>
                                            <div className="font-medium text-slate-800 flex items-center gap-2">
                                                {t(field.label)}
                                                {field.required && <span className="text-xs font-bold text-red-500 px-1.5 py-0.5 bg-red-50 rounded">{t('tools.required')}</span>}
                                            </div>
                                            {field.description && (
                                                <div className="text-xs text-slate-500 mt-1">{t(field.description)}</div>
                                            )}
                                        </div>
                                        <div>
                                            <select
                                                className={`w-full p-2.5 rounded-lg border text-sm ${mapping[field.id] ? 'border-indigo-300 bg-indigo-50/30' :
                                                    field.required ? 'border-red-300 bg-red-50/30' : 'border-slate-200 bg-white'
                                                    } focus:ring-2 focus:ring-indigo-500 outline-none`}
                                                value={mapping[field.id] || ''}
                                                onChange={(e) => handleMappingChange(field.id, e.target.value)}
                                            >
                                                <option value="">{t('tools.ignored')}</option>
                                                {csvHeaders.map(header => (
                                                    <option key={header} value={header}>{header}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="flex justify-between items-center pt-4 border-t border-slate-200">
                            <button
                                onClick={() => setStep('upload')}
                                className="px-5 py-2.5 bg-white border border-slate-200 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-50 transition-colors"
                            >
                                {t('common:back')}
                            </button>
                            <button
                                onClick={() => setStep('preview')}
                                disabled={!isValidMapping}
                                className={`px-5 py-2.5 rounded-xl text-sm font-bold shadow-sm transition-all flex items-center gap-2 ${isValidMapping
                                    ? 'bg-black text-white hover:bg-slate-800 shadow-lg'
                                    : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                                    }`}
                            >
                                {t('tools.continue_preview')} <ArrowRight size={16} />
                            </button>
                        </div>
                    </div>
                )}

                {step === 'preview' && (
                    <div className="max-w-6xl mx-auto mt-4">
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h3 className="text-lg font-bold text-slate-800">{t('tools.preview_title')}</h3>
                                <p className="text-sm text-slate-500">
                                    {t('tools.preview_stats', { valid: parsedClients.filter(c => c.isValid).length, error: parsedClients.filter(c => !c.isValid).length })}
                                </p>
                            </div>
                        </div>

                        {parsedClients.filter(c => !c.isValid).length > 0 && (
                            <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-xl flex items-start gap-3">
                                <AlertTriangle className="text-orange-500 flex-shrink-0 mt-0.5" size={20} />
                                <div>
                                    <h4 className="font-bold text-orange-800">{t('tools.preview_errors_title')}</h4>
                                    <p className="text-sm text-orange-700 mt-1">
                                        {t('tools.preview_errors_desc')}
                                    </p>
                                </div>
                            </div>
                        )}

                        {error && (
                            <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg flex items-start gap-3 border border-red-200">
                                <AlertTriangle className="flex-shrink-0 mt-0.5" size={18} />
                                <span className="text-sm font-medium">{error}</span>
                            </div>
                        )}

                        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-6">
                            <div className="overflow-x-auto">
                                <table className="w-full text-left text-sm whitespace-nowrap">
                                    <thead className="bg-slate-50 text-slate-500 border-b border-slate-200">
                                        <tr>
                                            <th className="px-4 py-3 font-medium">{t('tools.preview_columns.status')}</th>
                                            <th className="px-4 py-3 font-medium">{t('tools.preview_columns.row')}</th>
                                            <th className="px-4 py-3 font-medium">{t('tools.preview_columns.company')}</th>
                                            <th className="px-4 py-3 font-medium">{t('tools.preview_columns.vat')}</th>
                                            <th className="px-4 py-3 font-medium">{t('tools.preview_columns.email')}</th>
                                            <th className="px-4 py-3 font-medium">{t('tools.preview_columns.errors')}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {parsedClients.slice(0, 50).map((client, idx) => (
                                            <tr key={idx} className={client.isValid ? 'hover:bg-slate-50' : 'bg-red-50/50'}>
                                                <td className="px-4 py-3">
                                                    {client.isValid ? (
                                                        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-semibold bg-green-100 text-green-700">
                                                            <CheckCircle size={14} /> {t('tools.ready')}
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-semibold bg-red-100 text-red-700">
                                                            <X size={14} /> {t('tools.preview_error')}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-slate-500">#{client.rowNumber}</td>
                                                <td className="px-4 py-3 font-medium text-slate-800">{client.companyName || '-'}</td>
                                                <td className="px-4 py-3 font-mono text-slate-600">{client.vatNumber || '-'}</td>
                                                <td className="px-4 py-3 text-slate-600">{client.email || '-'}</td>
                                                <td className="px-4 py-3">
                                                    {client.errors.length > 0 && (
                                                        <span className="text-red-500 text-xs">
                                                            {client.errors.join(', ')}
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {parsedClients.length > 50 && (
                                <div className="p-4 bg-slate-50 border-t border-slate-200 text-center text-sm text-slate-500">
                                    {t('tools.preview_limit_warning', { total: parsedClients.length })}
                                </div>
                            )}
                        </div>

                        <div className="bg-slate-50 p-4 border border-slate-200 rounded-xl mb-6">
                            <label className="flex items-center gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={sendInvites}
                                    onChange={(e) => setSendInvites(e.target.checked)}
                                    className="w-5 h-5 rounded border-slate-300 text-black focus:ring-black"
                                />
                                <span className="text-sm font-medium text-slate-700">
                                    {t('tools.send_invites_option')}
                                </span>
                            </label>
                        </div>

                        <div className="flex justify-between items-center pt-4 border-t border-slate-200">
                            <button
                                onClick={() => setStep('mapping')}
                                className="px-5 py-2.5 bg-white border border-slate-200 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-50 transition-colors"
                            >
                                {t('tools.back_to_mapping')}
                            </button>
                            <button
                                onClick={handleImportSubmit}
                                disabled={parsedClients.filter(c => c.isValid).length === 0}
                                className={`px-5 py-2.5 rounded-xl text-sm font-bold shadow-sm transition-all flex items-center gap-2 ${parsedClients.filter(c => c.isValid).length > 0
                                    ? 'bg-black text-white hover:bg-slate-800 shadow-lg'
                                    : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                                    }`}
                            >
                                {t('tools.start_import', { count: parsedClients.filter(c => c.isValid).length })} <ArrowRight size={16} />
                            </button>
                        </div>
                    </div>
                )}

                {step === 'importing' && (
                    <div className="flex flex-col items-center justify-center py-20">
                        <Loader2 className="animate-spin text-indigo-600 mb-6" size={48} />
                        <h3 className="text-xl font-bold text-slate-800 mb-2">{t('tools.importing_title')}</h3>
                        <p className="text-slate-500 text-center max-w-md">
                            {t('tools.importing_desc')}
                        </p>
                    </div>
                )}

                {step === 'summary' && importResult && (
                    <div className="max-w-2xl mx-auto mt-8">
                        <div className="text-center mb-10">
                            <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                                <CheckCircle size={40} />
                            </div>
                            <h2 className="text-3xl font-bold text-slate-800 mb-2">{t('tools.summary_title')}</h2>
                            <p className="text-lg text-slate-500">
                                {t('tools.summary_desc')}
                            </p>
                        </div>

                        <div className="grid grid-cols-3 gap-4 mb-8">
                            <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200 text-center">
                                <div className="text-3xl font-black text-slate-800 mb-1">{importResult.created}</div>
                                <div className="text-sm font-medium text-slate-500">{t('tools.summary_new')}</div>
                            </div>
                            <div className="bg-blue-50 p-6 rounded-2xl border border-blue-100 text-center">
                                <div className="text-3xl font-black text-blue-800 mb-1">{importResult.updated}</div>
                                <div className="text-sm font-medium text-blue-600">{t('tools.summary_updated')}</div>
                            </div>
                            <div className="bg-red-50 p-6 rounded-2xl border border-red-100 text-center">
                                <div className="text-3xl font-black text-red-800 mb-1">{importResult.failed}</div>
                                <div className="text-sm font-medium text-red-600">{t('tools.summary_failed')}</div>
                            </div>
                        </div>

                        {importResult.failed > 0 && (
                            <div className="bg-white border border-slate-200 rounded-xl p-6 mb-8 flex items-center justify-between">
                                <div>
                                    <h4 className="font-bold text-slate-800">{t('tools.error_report_title')}</h4>
                                    <p className="text-sm text-slate-500 max-w-md mt-1">
                                        {t('tools.error_report_desc')}
                                    </p>
                                </div>
                                <button
                                    onClick={downloadErrorsCsv}
                                    className="px-5 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-xl text-sm font-bold hover:bg-slate-50 flex items-center gap-2 whitespace-nowrap"
                                >
                                    <Download size={16} /> {t('tools.download_error_log')}
                                </button>
                            </div>
                        )}

                        <div className="text-center">
                            <button
                                onClick={onBack}
                                className="px-8 py-3 bg-black text-white hover:bg-slate-800 rounded-xl text-sm font-bold shadow-lg transition-all"
                            >
                                {t('tools.back_to_settings')}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Save Profile Modal */}
            {showSaveModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden">
                        <div className="p-6">
                            <h3 className="text-lg font-bold text-slate-800 mb-2">{t('tools.save_profile_title')}</h3>
                            <p className="text-sm text-slate-500 mb-4">
                                {t('tools.save_profile_desc')}
                            </p>
                            <input
                                type="text"
                                placeholder={t('tools.profile_name_placeholder')}
                                value={newProfileName}
                                onChange={(e) => setNewProfileName(e.target.value)}
                                className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-black outline-none mb-6"
                                autoFocus
                            />
                            <div className="flex gap-3">
                                <button
                                    onClick={() => setShowSaveModal(false)}
                                    className="flex-1 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-medium transition-colors"
                                >
                                    {t('common:cancel')}
                                </button>
                                <button
                                    onClick={handleSaveProfile}
                                    disabled={!newProfileName.trim() || isSavingProfile}
                                    className="flex-1 px-4 py-2.5 bg-black hover:bg-slate-800 text-white rounded-xl font-bold transition-colors disabled:opacity-50 flex items-center justify-center"
                                >
                                    {isSavingProfile ? <Loader2 size={18} className="animate-spin" /> : t('common:save')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
